import { LayoutDashboard, Briefcase, Users, Users2, FileText, Ticket, KanbanSquare, Siren, KeyRound, Newspaper, GraduationCap, FolderKanban, BarChart3, BookOpen, Library, FileBox, Award, UserRound, Route, Clock, ListTodo, Gauge, UsersRound, LayoutTemplate, ScrollText, Landmark } from "lucide-react";

export function hasPermission(permissions: string[], required: readonly string[]) {
  if (required.length === 0) return true;
  return required.some(p => permissions.includes(p));
}

export function isAdminLike(permissions: string[]) {
  return hasPermission(permissions, ["users:manage", "settings:manage", "pm:settings_manage"]);
}

/** Sidebar entries admins see without "Show more", by the id SidebarNav gives them. */
export const DEFAULT_DAILY: ReadonlySet<string> = new Set([
  "/?view=dashboard",
  "/my-space",
  "/clients",
  "/support-tickets",
  "/manager",
  "g:escalations",
  "g:projects",
  "g:bexio",
]);

/** A person's own choice (stored by useSidebarDaily) wins over the default. */
export function isItemDaily(id: string, pinned: Record<string, boolean> = {}) {
  return pinned[id] ?? DEFAULT_DAILY.has(id);
}

/** Non-admins see everything; admins see their daily items and the rest under "Show more". */
export function splitDaily<T extends { id: string }>(items: readonly T[], pinned: Record<string, boolean>, isAdmin: boolean) {
  if (!isAdmin) return { daily: [...items], more: [] as T[] };
  return { daily: items.filter((i) => isItemDaily(i.id, pinned)), more: items.filter((i) => !isItemDaily(i.id, pinned)) };
}

export const NAV_ITEMS = [
  { href: "/?view=dashboard", label: "Dashboard", icon: LayoutDashboard, required: ["articles:view", "tickets:view", "escalations:view", "users:approve", "deletion_requests:review"] },
  { href: "/my-space", label: "My Space", icon: UserRound, required: [], memberOnly: true },
  { href: "/applications", label: "Applications", icon: Briefcase, required: ["applications:view"] },
  { href: "/clients", label: "Clients", icon: Users2, required: ["clients:view"] },
  { href: "/articles", label: "Articles", icon: FileText, required: ["articles:view"] },
  { href: "/support-tickets", label: "Support Tickets", icon: Ticket, required: ["tickets:view"] },
  { href: "/manager", label: "Manager home", icon: LayoutDashboard, required: ["reports:view", "timeoff:approve", "pm:capacity_view"] },
];

// Escalation settings belongs with the escalation workflow, alongside its
// list, insights, and runbooks.

export const BEXIO_CHILDREN = [
  { href: "/bexio", label: "My time", icon: Clock, required: ["time:log"] },
  { href: "/bexio/recap", label: "Daily recap", icon: ScrollText, required: ["time:recap"] },
  { href: "/bexio/team", label: "Team schedule", icon: UsersRound, required: ["time:view_all", "pm:capacity_view", "pm:contracts_manage", "timeoff:approve", "pm:settings_manage"] },
  { href: "/bexio/centre", label: "Bexio centre", icon: Landmark, required: ["bexio:issues_view", "pm:settings_manage"] },
];

export const PROJECTS_CHILDREN = [
  { href: "/projects", label: "All projects", icon: FolderKanban, required: ["projects:view"] },
  { href: "/projects/my-tasks", label: "My tasks", icon: ListTodo, required: ["projects:view"] },
  { href: "/projects/insights", label: "Project insights", icon: Gauge, required: ["pm:budget_view", "pm:health_view"] },
  { href: "/projects/templates", label: "Templates", icon: LayoutTemplate, required: ["projects:templates_manage"] },
];

export const ACCESS_CHILDREN = [
  { href: "/admin/access", label: "Give access", icon: KeyRound, required: ["access:manage"] },
  { href: "/admin/access-requests", label: "Access requests", icon: KeyRound, required: ["access:manage"] },
  { href: "/admin/roles", label: "Roles", icon: KeyRound, required: ["access:manage"] },
  { href: "/admin/access-log", label: "Access log", icon: KeyRound, required: ["access:manage"] },
  { href: "/admin/activity-log", label: "Activity log", icon: KeyRound, required: ["audit:view"] },
];

export const ESCALATIONS_CHILDREN = [
  { href: "/escalations", label: "All escalations", icon: Siren, required: ["escalations:view"] },
  { href: "/escalations/insights", label: "Insights", icon: BarChart3, required: ["escalations:view"] },
  { href: "/escalations/runbooks", label: "Runbooks", icon: BookOpen, required: ["escalations:view"] },
];

export const ONBOARDING_CHILDREN = [
  { href: "/training", label: "Training Hub", icon: LayoutDashboard, required: ["onboarding:library_manage", "onboarding:own", "onboarding:insights"] },
  { href: "/training/my/plan", label: "My journey", icon: Route, required: ["onboarding:view"] },
  { href: "/training/sessions", label: "Sessions to deliver", icon: Users, required: ["onboarding:view"] },
  { href: "/training/library", label: "Training library", icon: Library, required: ["onboarding:view", "onboarding:library_manage"] },
  { href: "/training/programs", label: "Programs", icon: FileBox, required: ["onboarding:view", "onboarding:library_manage"] },
  { href: "/training/credentials", label: "Credentials", icon: Award, required: ["training:credentials_manage"] },
  { href: "/training/trainees", label: "Trainees", icon: Users2, required: ["onboarding:view"] },
];

export const SHOW_COMING_SOON = false;
export const COMING_SOON_ITEMS = [
  { label: "Knowledge Transfer", icon: GraduationCap, slug: "knowledge-transfer" },
  { label: "News", icon: Newspaper, slug: "news" },
  { label: "Project management", icon: KanbanSquare, slug: "project-management" },
];

export const COLLAPSED_WIDTH = 64;
export const EXPANDED_WIDTH = 272;
export const TRANSITION = "duration-200 ease-in-out";
